import { createContext, useContext, useState, useEffect, type ReactNode } from 'react';
import { authService, type User } from '../services/auth.service';
import { restaurantService } from '../services/restaurant.service';
import { receiveTokenHandoff } from '../utils/tokenHandoff';
import { authStorage, type SessionType } from '../utils/authStorage';

interface AuthContextType {
    user: User | null;
    isAuthenticated: boolean;
    isLoading: boolean;
    login: (email: string, password: string, sessionType?: SessionType) => Promise<User>;
    logout: () => void;
    checkAuth: () => Promise<void>;
    setUser: (user: User | null) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
    const [user, setUser] = useState<User | null>(null);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        const initAuth = async () => {
            const token = authStorage.getToken();

            if (token) {
                try {
                    const response = await authService.getProfile();
                    setUser(response.data.user);
                    authStorage.setUser(JSON.stringify(response.data.user));
                } catch (error: any) {
                    console.error("Failed to fetch profile", error);
                    // Differentiate between Network Error and 401 Unauthorized
                    const isNetworkError = !error.response || error.code === 'ERR_NETWORK';

                    if (isNetworkError) {
                        console.warn("Network error during initAuth, falling back to cached user if available");
                        const cachedUser = authStorage.getUser();
                        if (cachedUser) {
                            try {
                                setUser(JSON.parse(cachedUser));
                            } catch(e) { }
                        }
                    } else {
                        authStorage.clearCurrentTab();
                        setUser(null);
                    }
                }
            }
            setIsLoading(false);
        };
        initAuth();
    }, []);

    // Security: if another tab logs in/out (token changes in localStorage), force
    // this tab back to the login screen instead of silently keeping the old
    // session's page displayed while requests start using the new token.
    // Exception: KDS and display screens should NEVER be kicked out by another tab.
    useEffect(() => {
        const onStorage = (e: StorageEvent) => {
            if (e.key === 'token') {
                const isKDSOrDisplay = 
                    window.location.pathname.includes('/kds') ||
                    window.location.pathname.includes('/production') ||
                    window.location.pathname.includes('/display');
                if (isKDSOrDisplay) return;

                setUser(null);
            }
        };
        window.addEventListener('storage', onStorage);
        return () => window.removeEventListener('storage', onStorage);
    }, []);

    // Idle timeout: a POS/PIN (ephemeral) session left untouched auto-logs-out —
    // a station walked away from between customers must not stay unlocked for
    // the next person. Scoped to this tab only (sessionStorage) and skipped
    // entirely for manager/cloud (persistent) sessions, and KDS/kitchen displays.
    // Duration and on/off are per-restaurant (restaurants.pin_lock_enabled /
    // pin_lock_seconds, configured from POSRestaurantDetails.tsx) — fetched here
    // via the same public lookup POSContext uses, since AuthContext sits above
    // POSContext in the tree and can't read its state. Defaults to enabled/60s
    // if the fetch fails, matching the previous hardcoded behavior.
    useEffect(() => {
        if (!user || authStorage.getSessionType() !== 'ephemeral') return;

        // Skip idle timeout on KDS, kitchen display, and customer display screens
        const isKDSOrDisplay =
            window.location.pathname.includes('/kds') ||
            window.location.pathname.includes('/production') ||
            window.location.pathname.includes('/display') ||
            user.role === 'kitchen' ||
            user.role === 'cook' ||
            (user as any).department === 'kitchen';

        if (isKDSOrDisplay) return;

        let cancelled = false;
        let timer: ReturnType<typeof setTimeout>;
        let cleanupListeners: (() => void) | undefined;

        const start = async () => {
            const restaurantId = user.restaurant_id ?? localStorage.getItem('pos_restaurant_id');
            if (!restaurantId) return;

            let enabled = true;
            let seconds = 60;
            try {
                const res = await restaurantService.getByIdPublic(String(restaurantId));
                enabled = res.data.restaurant.pin_lock_enabled ?? true;
                seconds = res.data.restaurant.pin_lock_seconds ?? 60;
            } catch {
                // Network/lookup failure: keep the previous default behavior (enabled, 60s)
            }

            if (cancelled || !enabled) return;

            const resetTimer = () => {
                clearTimeout(timer);
                timer = setTimeout(() => {
                    const currentIsKDS =
                        window.location.pathname.includes('/kds') ||
                        window.location.pathname.includes('/production') ||
                        window.location.pathname.includes('/display');
                    if (currentIsKDS) return;

                    authStorage.clearCurrentTab();
                    setUser(null);
                }, seconds * 1000);
            };

            const activityEvents = ['mousedown', 'mousemove', 'keydown', 'touchstart', 'wheel'];
            activityEvents.forEach((event) => window.addEventListener(event, resetTimer));
            resetTimer();
            cleanupListeners = () => activityEvents.forEach((event) => window.removeEventListener(event, resetTimer));
        };
        start();

        return () => {
            cancelled = true;
            clearTimeout(timer);
            cleanupListeners?.();
        };
    }, [user]);

    // Cross-app SSO: if opened by the accountant portal for a token handoff,
    // announce readiness and reload once the token lands in localStorage —
    // reuses the normal initAuth() path above instead of duplicating it.
    useEffect(() => {
        return receiveTokenHandoff((token) => {
            localStorage.setItem('token', token);
            window.location.reload();
        });
    }, []);

    const checkAuth = async () => {
        const token = authStorage.getToken();
        if (token) {
            try {
                const response = await authService.getProfile();
                setUser(response.data.user);
                authStorage.setUser(JSON.stringify(response.data.user));
            } catch (error: any) {
                console.error("Failed to fetch profile", error);
                const isNetworkError = !error.response || error.code === 'ERR_NETWORK';
                if (isNetworkError) {
                    const cachedUser = authStorage.getUser();
                    if (cachedUser) {
                        try {
                            setUser(JSON.parse(cachedUser));
                        } catch(e) {}
                    }
                } else {
                    authStorage.clearCurrentTab();
                    setUser(null);
                }
            }
        } else {
            setUser(null);
        }
    };

    const login = async (email: string, password: string, sessionType: SessionType = 'persistent'): Promise<User> => {
        const response = await authService.login(email, password);
        authStorage.setAuth(response.data.token, JSON.stringify(response.data.user), sessionType);
        setUser(response.data.user);
        return response.data.user;
    };

    const logout = () => {
        authService.logout();
        setUser(null);
    };

    return (
        <AuthContext.Provider value={{ user, isAuthenticated: !!user, isLoading, login, logout, checkAuth, setUser }}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};
